/**
 * The tenant alert engine's codes about the reconcile loop: whether it is
 * holding a cluster, reaching it, seeing it, and converging it.
 *
 * Fingerprint for every code here: the tenant (ORGANIZATION scope), entity
 * `vibe_cluster`, entityId the cluster's organizationId — one row per tenant.
 */
import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

export const VIBE_ALERT_RECONCILE_ISSUES = {
  /**
   * Not paging: a pause is set by an operator on purpose, so the person who can
   * act on an overdue one is the person who set it, and the row is where they
   * look. It costs convergence, not a customer request.
   */
  VIBE_ALERT_RECONCILE_PAUSED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "This tenant cluster's reconcile pause has outlived its window (the per-pause window in the tenant alert policy, or its default). A paused cluster is neither converged nor drift-checked, and looks healthy everywhere but the roster. The payload carries when it was paused, the reason recorded with the pause, and — under \"Held by this pause\" — every finding the pause is holding that only a converge clears (pending changes, the client group's drain posture), each of which would otherwise open a row of its own.",
    severity: "medium",
    remedy:
      "Read the pause reason on the payload and find who set it, and read the held findings: releasing the pause sets each of them converging. If the work it was held for is done, lift the pause on the cluster. If the pause is meant to last, grant it a longer window in `vibe.tenant_alerts.policy` → `pauseWindows`, keyed on the organization and this pause's own `reconcilePausedAt`, so it expires with this pause and no later one. This resolves on its own once the pause is lifted."
  },

  /**
   * Not paging: it costs this cluster its converge slot, which a human cannot get
   * back by being told sooner; the row stands until the window closes or the
   * pause lifts.
   */
  VIBE_ALERT_RECONCILE_PAUSED_IN_WINDOW: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "This tenant cluster is paused while the converge window is open, so it misses the one slot a disruptive change gets and waits for the next window. The payload carries when it was paused and when the window opened.",
    severity: "medium",
    remedy:
      "If the pause is deliberate and the cluster should skip this window, nothing is needed: the row resolves when the window closes. If the pause was forgotten, lift it while the window is still open and the reconcile loop converges the cluster in this window. This resolves on its own when the window closes or the pause lifts."
  },

  /**
   * Paging: in a working fleet no cluster goes unvisited this long, so one that
   * does means the reconcile sweep itself has stopped working — the loop every
   * tenant's convergence and drift repair stands on.
   */
  VIBE_ALERT_RECONCILE_VISIT_OVERDUE: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "The reconcile sweep has not reached this tenant cluster for longer than the converge policy allows (`convergePolicy.visitOverdueMinutes`). The sweep carries an unreached cluster to the head of its next tick, so a gap this long is the sweep not working: a budget that keeps running out before it, a tick that dies before it gets there, or a cron that stopped. The payload carries the last visit.",
    severity: "high",
    page: true,
    remedy:
      "Check the reconcile tick's cron execution log (VIBE_TENANT_CLUSTER_RECONCILE_TICK): whether it is firing at all, whether each tick ends on its budget, and whether one cluster's converge is eating the whole budget. A cron that stopped is re-enabled from the cron admin; a tick dying mid-run names its error in the worker log. This resolves on its own on the first visit."
  },

  /**
   * Paging: DEGRADED for a reason that is not routine pending changes, held past
   * the policy's window, is a cluster in fault — and the routine reason is
   * already filtered out by the detector, so this does not fire on every deploy.
   */
  VIBE_ALERT_CLUSTER_DEGRADED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "This tenant cluster's lifecycle status has been DEGRADED for a reason that is not routine pending changes, for longer than the policy's degraded window — or DEGRADED with nothing on the row saying why. The payload carries the status reason.",
    severity: "high",
    page: true,
    remedy:
      "Read the status reason on the payload: it is the reconcile loop's own words for the failed step (a stack error, an AWS limit, an unreachable agent). Fix that cause and let the next reconcile tick converge the cluster; an unclassified DEGRADED means the loop could not observe the cluster, so start from its last tick's log. This resolves on its own once the status is no longer a fault."
  },

  /**
   * Paging: an operator forced this converge because a person is waiting on it,
   * and the forced-converge dispatch starts one within seconds — so a force no
   * apply has begun on past its short window is the dispatch not working, and
   * nothing else will pick it up before the sweep's turn, up to two ticks later.
   */
  VIBE_ALERT_FORCED_CONVERGE_NOT_STARTED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "An operator forced this tenant cluster's converge and no apply has begun on it for longer than the converge policy allows (`convergePolicy.forcedNotStartedForMinutes`). The forced-converge dispatch starts a forced apply within seconds, so this is the dispatch not running, a lock it cannot take, or a step before the apply failing on every attempt. A forced apply that has begun and is still running does not raise this. The payload carries the forced reason and when the cluster began waiting.",
    severity: "high",
    page: true,
    remedy:
      "Check the forced-converge dispatch's cron execution log (VIBE_FORCED_CONVERGE_DISPATCH): whether it is firing, which tenants each run started, and what each visit counted — `unchanged` means another loop held the tenant's convergence lock or a brake stopped it, `failed` names the step. A cron that stopped is re-enabled from the cron admin. This resolves on its own once an apply begins, or the force leaves the row."
  },

  /**
   * Paging: every backend deploy moves the program under the whole fleet, and the
   * forced-converge dispatch visits a program-moved cluster the moment it sees one
   * — that visit is where whatever the new program changes is previewed and, when
   * it rolls nothing, applied. So a serving
   * cluster a later program has not observed past its window is the dispatch not
   * working, and the sweep that would otherwise reach it can be most of two ticks
   * away.
   */
  VIBE_ALERT_PROGRAM_MOVE_UNOBSERVED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "A later deploy of the backend's provisioner program than the one that last observed this serving tenant cluster has been running for longer than the converge policy allows (`convergePolicy.programMoveUnobservedForMinutes`), and no visit has observed the cluster with it. The forced-converge dispatch visits such a cluster within seconds, at most three at a time, and that visit previews the drift the deploy produced and applies it when it rolls nothing — so this is the dispatch not running, a preview failing on every attempt, or more moved clusters than the dispatch clears in that window. The payload carries the program that last observed the cluster and the one running now.",
    severity: "high",
    page: true,
    remedy:
      "Check the forced-converge dispatch's cron execution log (VIBE_FORCED_CONVERGE_DISPATCH): whether it is firing, and whether its runs list this tenant under program-moved. A tenant it visited whose preview failed is counted `failed` with the step named, and the tenant's own unobservable alarm counts the faults. A cron that stopped is re-enabled from the cron admin. This resolves on its own once a visit records the running program on the cluster."
  },

  /**
   * Not paging: a late converge is a policy miss on a serving cluster, and the
   * loop applies it on its own schedule. On a PAUSED cluster it opens no row of
   * its own: the pause is what holds it, so it is a named line on the pause's.
   */
  VIBE_ALERT_CONVERGE_PENDING: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "This tenant cluster's pending changes have waited longer than the converge policy allows (`convergePolicy.pendingForMinutes`), so the converge policy has failed for it: a fault on every attempt, or a sweep that never reaches it. The payload carries when the wait began and the pending changes. While a reconcile pause holds the cluster this opens no row of its own: it is listed on the pause's row, and it returns here the tick the pause lifts if it is still due.",
    severity: "medium",
    remedy:
      "Read the pending changes and the `converge disruption` verdict on the payload. A disruptive change waits for the converge window or its daily overdue slot; if both have passed, the reconcile tick's log for this cluster names why the apply did not happen. This resolves on its own once the cluster converges."
  }
} as const satisfies Record<string, IssueDefinition>;
